var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { Collapsible, Confirmation, DNABox, DNAChip, Icon, Iffy, luxColors, } from '@alucio/lux-ui';
import Menu from '../Menu/Menu';
import { MODE_TYPE, useLocalAudioToggle, useLocalVideoToggle, useRoomState } from '../../../main';
import API, { graphqlOperation } from '@aws-amplify/api';
import { virtualStartRecording } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import { useVideoContext, useAttendeeShareScreen, useMeetingControls, SelectedRightSideBarComponentEnum, } from '@alucio/video';
import useRecording, { RecordingStatus } from '../../../hooks/useIsRecording';
import DNARecordingBadge, { DNARecordingBadgeMode } from '../../DNA/DNARecordingBadge/DNARecordingBadge';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
const styles = StyleSheet.create({
    attendeeCountLabel: {
        color: colors['color-text-basic'],
        fontSize: 15,
        marginLeft: 12,
        minWidth: 58,
        cursor: 'default',
    },
    attendeeIcon: {
        color: colors['color-text-basic'],
        height: 20,
        width: 20,
        cursor: 'default',
    },
    attendeeCountTooltip: {
        color: luxColors.info.primary,
    },
    bezel: {
        alignItems: 'center',
        alignSelf: 'center',
        backgroundColor: colors['color-brand-900'],
        borderTopLeftRadius: 15,
        borderTopRightRadius: 15,
        flex: 1,
        bottom: 0,
        flexDirection: 'column',
        position: 'absolute',
        width: 104,
    },
    bezelIcon: {
        color: colors['color-text-basic'],
    },
    endCall: {
        width: 160,
    },
    endCallButton: {
        borderRadius: 30,
        marginRight: 16,
        zIndex: 1,
        height: 32,
    },
    headerContainer: {
        width: '100%',
        justifyContent: 'space-between',
    },
    infoIcon: {
        color: colors['color-text-basic'],
        height: 20,
        width: 20,
    },
    infoText: {
        color: luxColors.info.primary,
    },
    infoLabel: {
        color: luxColors.info.primary,
        opacity: 0.48,
        marginTop: 22,
    },
    copyLink: {
        color: luxColors.primary.primary,
        marginLeft: 16,
    },
    menuDivider: {
        borderWidth: 0,
        borderLeftWidth: 1,
        borderLeftColor: luxColors.info.primary,
        height: '70%',
        marginTop: 8,
        marginBottom: 8,
        marginRight: 18,
        marginLeft: 18,
        opacity: 0.16,
    },
    menuOptionChip: {
        position: 'absolute',
        top: 0,
        right: 10,
        height: 16,
        fontSize: 11,
        justifyContent: 'center',
        alignItems: 'center',
    },
    recordStyle: {
        width: 70,
        height: 24,
        marginLeft: 16,
    },
});
export var PresentationMenuOptionsEnum;
(function (PresentationMenuOptionsEnum) {
    PresentationMenuOptionsEnum["MY_CONTENT"] = "my_content";
    PresentationMenuOptionsEnum["CHAT"] = "chat";
    PresentationMenuOptionsEnum["SHARE_SCREEN"] = "share_screen";
    PresentationMenuOptionsEnum["NOTES"] = "notes";
    PresentationMenuOptionsEnum["RECORD"] = "record";
    PresentationMenuOptionsEnum["FULL_SCREEN"] = "full_screen";
    PresentationMenuOptionsEnum["END_CALL"] = "end_call";
    PresentationMenuOptionsEnum["TOGGLE_SLIDES"] = "toggle_slides";
})(PresentationMenuOptionsEnum || (PresentationMenuOptionsEnum = {}));
var MODE;
(function (MODE) {
    MODE["PRESENTATION"] = "presentation";
    MODE["SCREEN_SHARE"] = "screen-share";
    MODE["RECORD"] = "record";
    MODE["STOP_RECORD"] = "stop record";
    MODE["NONE"] = "none";
})(MODE || (MODE = {}));
const showConfirmationInitialState = {
    visible: false,
    text: '',
    header: '',
    successText: '',
    cancelText: '',
    mode: MODE.NONE,
};
function buildShowInformation(mode) {
    switch (mode) {
        case MODE.PRESENTATION: {
            return {
                mode: mode,
                text: 'By stopping screenshare, you can access My Content',
                header: 'Stop screenshare to continue?',
                successText: 'Stop share',
                cancelText: 'Cancel',
            };
        }
        case MODE.SCREEN_SHARE: {
            return {
                mode: mode,
                text: 'By stopping My Content, you can access Share Screen',
                header: 'Stop My Content to continue?',
                successText: 'Stop my content',
                cancelText: 'Cancel',
            };
        }
        case MODE.RECORD: {
            return {
                mode: mode,
                text: 'Please obtain consent to record this meeting from all participants, including attendees who join late.',
                header: 'Ask for consent',
                successText: 'Accept',
                cancelText: 'Decline',
            };
        }
        case MODE.STOP_RECORD: {
            return {
                mode: mode,
                text: 'This meeting will be saved in Beacon.',
                header: 'Stop recording this meeting?',
                successText: 'Stop recording',
                cancelText: 'Cancel',
            };
        }
        default: break;
    }
}
/*
  helper to find if a menu is selected, for instance, currently my content is selected and i will move to my sharing
*/
function isMenuActive(PresentationMenuOptions, id) {
    const isMenuItemSelected = PresentationMenuOptions.find((item) => (item.id === id));
    return (isMenuItemSelected === null || isMenuItemSelected === void 0 ? void 0 : isMenuItemSelected.active) === true;
}
function startRecording(sessionId, meetingURL) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield API.graphql(graphqlOperation(virtualStartRecording, {
            sessionId: sessionId,
            meetingHostname: meetingURL.replace('https://', '').split('/')[0],
        }));
        if (response.data.virtualStartRecording.connectURL) {
            console.warn(response.data.virtualStartRecording.connectURL);
        }
    });
}
/**
 * Instead of set one to one each selected property, we send an array of the
 * selected elemnts
 * @param PresentationSelectedOptions: selected items to set as activated
 * @param Property: Property to activate
 */
function setSelectedMenuProperty(PresentationMenuOptions, PresentationSelectedOptions, property) {
    if (PresentationSelectedOptions.length === 0) {
        PresentationMenuOptions.forEach((menu) => (menu[property] = false));
    }
    PresentationMenuOptions.forEach((menu) => {
        const exist = PresentationSelectedOptions.find(item => item === menu.id);
        exist ? menu[property] = true : menu[property] = false;
    });
    return PresentationMenuOptions;
}
function MeetingControls() {
    const { audioFiles, disabledOptions, displayContentPanel, hideContentPanel, isFullScreen, isHostPresenting, isOtherUserScreenSharing, localPhoneAudio, meetingURL, mode, onCallEnd, onError, onShowContentPanel, onStopPresenting, onRecordingChange, presentationSelectedOptions, setIsFullScreen, shareScreen, togglePhoneAudio, toggleRightBarHandler, toggleSlidesHandler, virtual, selectTabOption, } = useMeetingControls();
    const roomState = useRoomState();
    const [isAudioEnabled, toggleAudioEnabled] = useLocalAudioToggle();
    const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();
    const [isHeaderHidden, setIsHeaderHidden] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(showConfirmationInitialState);
    const { sendStopAllShareScreen } = useAttendeeShareScreen();
    const { unreadMsgCount, chatVisible } = useVideoContext();
    const { isRecording: recordingStatus, recordingPaused, recordingStopped, recordingResumed, preparingRecording, } = useRecording({ isHost: mode === MODE_TYPE.HOST });
    const isPreparing = recordingStatus === RecordingStatus.PREPARING;
    const isRecording = [
        RecordingStatus.RECORDING,
        RecordingStatus.RECORDER_RESUMED,
    ].includes(recordingStatus);
    useEffect(() => {
        setIsHeaderHidden(!!isFullScreen);
        if (isFullScreen) {
            document === null || document === void 0 ? void 0 : document.documentElement.requestFullscreen();
        }
        else if (document === null || document === void 0 ? void 0 : document.fullscreenElement) {
            document.exitFullscreen();
        }
    }, [isFullScreen]);
    useEffect(() => {
        function fullScreenListenerHandler() {
            if (!(document === null || document === void 0 ? void 0 : document.fullscreenElement)) {
                setIsFullScreen === null || setIsFullScreen === void 0 ? void 0 : setIsFullScreen(false);
            }
            else if ((document === null || document === void 0 ? void 0 : document.fullscreenElement) && mode === MODE_TYPE.GUEST) {
                setIsFullScreen === null || setIsFullScreen === void 0 ? void 0 : setIsFullScreen(true);
            }
        }
        document === null || document === void 0 ? void 0 : document.addEventListener('fullscreenchange', fullScreenListenerHandler);
        return () => document === null || document === void 0 ? void 0 : document.removeEventListener('fullscreenchange', fullScreenListenerHandler);
    }, []);
    useEffect(() => {
        if (unreadMsgCount > 0) {
            setIsHeaderHidden(false);
        }
    }, [unreadMsgCount]);
    useEffect(() => {
        roomState === 'connected' && onRecordingChange && onRecordingChange(recordingStatus);
        if (mode === MODE_TYPE.HOST && roomState === 'connected') {
            switch (recordingStatus) {
                case RecordingStatus.RECORDING:
                    trackActionInAnalytics('RECORD');
                    break;
                case RecordingStatus.RECORDER_PAUSED:
                    trackActionInAnalytics('RECORD_PAUSE');
                    break;
                case RecordingStatus.RECORDER_RESUMED:
                    trackActionInAnalytics('RECORD_RESUME');
            }
        }
    }, [recordingStatus]);
    // Player Options
    function onPlayerSelectedOption(selected) {
        // TODO: refactor this so that it is not reliant on an id which is dependant on the design aesthetic
        // Would recommend defining menuoptions as TS classes and using instanceof typeguard here
        if (['Mute', 'Unmute'].includes(selected.title)) {
            toggleAudioEnabled();
        }
        else if (['Stop video', 'Start video'].includes(selected.title)) {
            toggleVideoEnabled();
        }
        onPresentationSelectedOption(selected);
    }
    const muteAudioOption = {
        id: 'mute',
        title: 'Mute',
        icon: 'microphone',
    };
    if (!isAudioEnabled) {
        muteAudioOption.title = 'Unmute';
        muteAudioOption.icon = 'microphone-off';
        muteAudioOption.selectedColor = colors['color-danger-500'];
    }
    const muteVideoOption = {
        id: 'video',
        title: 'Stop video',
        icon: 'video',
    };
    if (!isVideoEnabled) {
        muteVideoOption.title = 'Start video';
        muteVideoOption.icon = 'video-off';
        muteVideoOption.selectedColor = colors['color-danger-500'];
    }
    const playerMenuOptions = [
        muteVideoOption,
    ];
    if (!localPhoneAudio) {
        playerMenuOptions.unshift(muteAudioOption);
    }
    function onPresentationSelectedOption(selected) {
        switch (selected.id) {
            case PresentationMenuOptionsEnum.MY_CONTENT:
                if (!isMenuActive(PresentationMenuOptions, 'share_screen')) {
                    onShowContentPanel && onShowContentPanel();
                }
                else {
                    setShowConfirmation(Object.assign(Object.assign(Object.assign({}, showConfirmation), { visible: true }), buildShowInformation(MODE.PRESENTATION)));
                }
                break;
            case PresentationMenuOptionsEnum.SHARE_SCREEN:
                if (!isMenuActive(PresentationMenuOptions, 'my_content')) {
                    sendStopAllShareScreen();
                    shareScreen && shareScreen();
                    onStopPresenting && onStopPresenting();
                }
                else { // Do this without confirmation, peer product decision
                    sendStopAllShareScreen();
                    shareScreen && shareScreen();
                    onStopPresenting && onStopPresenting();
                    hideContentPanel && hideContentPanel();
                }
                break;
            case PresentationMenuOptionsEnum.NOTES:
                selectTabOption(SelectedRightSideBarComponentEnum.NOTES);
                toggleRightBarHandler(SelectedRightSideBarComponentEnum.NOTES);
                break;
            case PresentationMenuOptionsEnum.FULL_SCREEN:
                setIsFullScreen === null || setIsFullScreen === void 0 ? void 0 : setIsFullScreen(!isFullScreen);
                break;
            case PresentationMenuOptionsEnum.CHAT:
                selectTabOption(SelectedRightSideBarComponentEnum.CHAT);
                toggleRightBarHandler(SelectedRightSideBarComponentEnum.CHAT);
                if (mode === MODE_TYPE.HOST && !chatVisible) {
                    trackActionInAnalytics('CHAT');
                }
                break;
            case PresentationMenuOptionsEnum.RECORD:
                if (isPreparing || isRecording) {
                    setShowConfirmation(Object.assign(Object.assign(Object.assign({}, showConfirmation), { visible: true }), buildShowInformation(MODE.STOP_RECORD)));
                }
                else {
                    setShowConfirmation(Object.assign(Object.assign(Object.assign({}, showConfirmation), { visible: true }), buildShowInformation(MODE.RECORD)));
                }
                break;
            case PresentationMenuOptionsEnum.END_CALL:
                onCallEnd();
                break;
            case PresentationMenuOptionsEnum.TOGGLE_SLIDES:
                toggleSlidesHandler();
                break;
        }
    }
    function trackActionInAnalytics(action) {
        analytics === null || analytics === void 0 ? void 0 : analytics.track(`VIRTUAL_${action}`, {
            action: action,
            category: 'VIRTUAL',
            sessionId: virtual.session.id,
        });
    }
    const PresentationMenuOptions = [];
    if (mode === MODE_TYPE.HOST) {
        PresentationMenuOptions.push({
            id: 'my_content',
            active: displayContentPanel,
            showText: true,
            title: 'Share content',
            icon: 'presentation-play',
        });
        PresentationMenuOptions.push({
            id: 'share_screen',
            title: 'Share screen',
            activeTitle: 'Stop share',
            icon: 'monitor-share',
        });
        PresentationMenuOptions.push({
            id: 'record',
            title: (isPreparing || isRecording) ? 'Stop' : 'Record',
            activeTitle: 'Stop',
            icon: (isPreparing || isRecording) ? 'stop-circle-outline' : 'radiobox-marked',
            selectedColor: (isPreparing || isRecording) ? colors['color-danger-500'] : undefined,
        });
    }
    PresentationMenuOptions.push({
        id: 'settings',
        title: 'Settings',
        icon: 'dots-vertical',
        settingsParams: {
            localPhoneAudio: localPhoneAudio,
            audioFiles: audioFiles,
            togglePhoneAudio: togglePhoneAudio,
        },
    });
    PresentationMenuOptions.push({
        id: 'end_call',
        title: mode === MODE_TYPE.HOST ? 'End meeting' : 'Leave meeting',
        icon: 'phone-hangup',
        backgroundColor: colors['color-danger-500'],
    });
    const LeftMenuOptions = [];
    const RightMenuOptions = [{
            id: 'chat',
            title: 'Chat',
            icon: 'chat-processing',
            chip: React.createElement(Iffy, { is: unreadMsgCount > 0 },
                React.createElement(DNAChip, { style: styles.menuOptionChip, appearance: "tag", status: "danger" }, unreadMsgCount.toString())),
        }];
    if (!isFullScreen) {
        LeftMenuOptions.push({
            id: 'view_compact',
            title: 'View',
            icon: 'view-compact',
            showText: true,
        });
    }
    // logic to show only when is presenting
    if (isHostPresenting && mode === MODE_TYPE.HOST) {
        LeftMenuOptions.unshift({
            id: 'toggle_slides',
            title: 'Slides',
            icon: 'table-column',
            showText: false,
        });
    }
    if (isHostPresenting && mode === MODE_TYPE.HOST) {
        RightMenuOptions.unshift({
            id: 'notes',
            title: 'Notes',
            icon: 'note-text',
        });
    }
    if (mode === MODE_TYPE.GUEST && (isHostPresenting || isOtherUserScreenSharing)) {
        PresentationMenuOptions.push({
            id: 'full_screen',
            title: isFullScreen ? 'Exit full screen' : 'Full screen',
            activeTitle: 'Exit full screen',
            icon: isFullScreen ? 'fullscreen-exit' : 'fullscreen',
        });
    }
    setSelectedMenuProperty(PresentationMenuOptions, presentationSelectedOptions, 'active');
    setSelectedMenuProperty(PresentationMenuOptions, disabledOptions, 'disabled');
    function onConfirmationSuccess() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (showConfirmation.mode === MODE.PRESENTATION) {
                onShowContentPanel && onShowContentPanel();
                sendStopAllShareScreen();
                shareScreen && shareScreen();
            }
            else if (showConfirmation.mode === MODE.SCREEN_SHARE) {
                sendStopAllShareScreen();
                shareScreen && shareScreen();
                onStopPresenting && onStopPresenting();
                hideContentPanel && hideContentPanel();
            }
            else if (showConfirmation.mode === MODE.RECORD) {
                setShowConfirmation(Object.assign(Object.assign({}, showConfirmationInitialState), { visible: false }));
                try {
                    // If it's the first time, call the API
                    // Otherwise, it means the recording was paused and needs to be resumed
                    if (recordingStatus === RecordingStatus.NOT_RECORDING) {
                        const status = preparingRecording();
                        // Do not fire this if the recorder user is already in the room
                        status === RecordingStatus.PREPARING &&
                            (yield startRecording((_a = virtual === null || virtual === void 0 ? void 0 : virtual.session) === null || _a === void 0 ? void 0 : _a.id, meetingURL.replace('https://', '').split('/')[0]));
                    }
                    else {
                        recordingResumed();
                    }
                }
                catch (ex) {
                    recordingStopped();
                    onError && onError('Unable to start recording. Please try again.', false);
                }
            }
            else if (showConfirmation.mode === MODE.STOP_RECORD) {
                // If the status is PREPARING, it means it never got to the RECORDING status so it needs to go back to NOT_RECORDING
                if (recordingStatus === RecordingStatus.PREPARING) {
                    recordingStopped();
                }
                else {
                    recordingPaused();
                }
            }
            setShowConfirmation(Object.assign(Object.assign({}, showConfirmationInitialState), { visible: false }));
        });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Iffy, { is: isFullScreen },
            React.createElement(TouchableOpacity, { onPress: () => setIsHeaderHidden(!isHeaderHidden) },
                React.createElement(DNABox, { testID: "bezel", style: styles.bezel },
                    React.createElement(Icon, { style: styles.bezelIcon, name: isHeaderHidden ? 'chevron-up' : 'chevron-down' })))),
        React.createElement(Collapsible, { collapsed: isHeaderHidden, allowOverflowOnVisible: true },
            React.createElement(React.Fragment, null,
                React.createElement(Iffy, { is: showConfirmation.visible },
                    React.createElement(Confirmation, { showCloseButton: true, text: showConfirmation.text, header: showConfirmation.header, successText: showConfirmation.successText, cancelText: showConfirmation.cancelText, onSuccess: onConfirmationSuccess, onCancel: () => setShowConfirmation(Object.assign(Object.assign({}, showConfirmation), { visible: false })) })),
                React.createElement(DNABox, { fill: true, appearance: "row", alignY: "center", style: { paddingHorizontal: 12, paddingVertical: 12 } },
                    React.createElement(Iffy, { is: mode === MODE_TYPE.HOST },
                        React.createElement(DNABox, { fill: true, alignY: "center", alignX: "start", appearance: "row" },
                            React.createElement(Menu, { menuOptions: LeftMenuOptions, onSelectedOption: onPresentationSelectedOption, disabled: roomState !== 'connected' }),
                            React.createElement(DNABox, { style: { marginLeft: 12 } },
                                React.createElement(Iffy, { is: [RecordingStatus.RECORDING, RecordingStatus.RECORDER_RESUMED].includes(recordingStatus) },
                                    React.createElement(DNARecordingBadge, { mode: DNARecordingBadgeMode.RECORDING })),
                                React.createElement(Iffy, { is: recordingStatus === RecordingStatus.PREPARING },
                                    React.createElement(DNARecordingBadge, { mode: DNARecordingBadgeMode.PREPARING })))),
                        React.createElement(DNABox, { alignY: "center", alignX: "center", spacing: "medium" },
                            React.createElement(Menu, { menuOptions: [...playerMenuOptions, ...PresentationMenuOptions], onSelectedOption: onPlayerSelectedOption, isHeaderHidden: isHeaderHidden, disabled: roomState !== 'connected' }))),
                    React.createElement(Iffy, { is: mode === MODE_TYPE.GUEST },
                        React.createElement(DNABox, { fill: true, alignY: "center", alignX: "start", appearance: "row" },
                            React.createElement(Menu, { menuOptions: LeftMenuOptions, onSelectedOption: onPresentationSelectedOption, disabled: roomState !== 'connected' }),
                            React.createElement(Iffy, { is: isRecording },
                                React.createElement(DNABox, { style: { marginLeft: 12 } },
                                    React.createElement(DNARecordingBadge, { mode: DNARecordingBadgeMode.RECORDING })))),
                        React.createElement(DNABox, { alignY: "center", alignX: "center", appearance: "row" },
                            React.createElement(Menu, { menuOptions: playerMenuOptions, onSelectedOption: onPlayerSelectedOption, isHeaderHidden: isHeaderHidden, disabled: roomState !== 'connected' }),
                            React.createElement(Menu, { menuOptions: PresentationMenuOptions, onSelectedOption: onPresentationSelectedOption, disabled: roomState !== 'connected' }))),
                    React.createElement(DNABox, { fill: true, alignY: "center", alignX: "end" },
                        React.createElement(Menu, { menuOptions: RightMenuOptions, onSelectedOption: onPresentationSelectedOption, disabled: roomState !== 'connected' })))))));
}
export default MeetingControls;
//# sourceMappingURL=MeetingControls.js.map